import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calculator, Save } from "lucide-react";

const monthsBetween = (targetIso) => {
  if (!targetIso) return 60;
  const d = new Date(targetIso);
  const now = new Date();
  const m = Math.max(1, Math.round((d - now) / (30.44 * 864e5)));
  return m;
};

const inr = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

export default function SipCalculatorDialog({ goal, open, onClose }) {
  const [mode, setMode] = useState("Flat SIP");
  const [rate, setRate] = useState(12);
  const [step, setStep] = useState(10);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const tenure = useMemo(() => monthsBetween(goal?.target_date), [goal]);

  useEffect(() => {
    if (!open || !goal) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/goals/${goal.id}/sip-calculate`, {
          target_amount: goal.target_amount,
          tenure_months: tenure,
          assumed_annual_return_pct: Number(rate),
          mode,
          step_up_pct: mode === "Step-Up SIP" ? Number(step) : null,
        });
        setResult(data);
      } catch (err) { /* silent — live recalc */ }
    }, 200);
    return () => clearTimeout(t);
  }, [open, goal, mode, rate, step, tenure]);

  const save = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await api.post(`/goals/${goal.id}/save-funding-plan`, {
        target_amount: goal.target_amount,
        target_date: goal.target_date,
        tenure_months: tenure,
        assumed_annual_return_pct: Number(rate),
        mode,
        step_up_pct: mode === "Step-Up SIP" ? Number(step) : null,
        required_monthly_sip: mode === "Flat SIP" ? result.flat_monthly_sip : result.year1_monthly_sip,
        year_wise_sip_schedule: result.year_wise_sip_schedule || null,
      });
      toast.success("Funding plan saved on the goal");
      onClose();
    } catch (err) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  if (!goal) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-4 w-4" /> SIP calculator · {goal.title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-3">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <Label>Target</Label>
              <div className="mt-2 h-10 rounded-md border border-border bg-secondary/40 px-3 flex items-center text-sm font-medium">{inr(goal.target_amount)}</div>
            </div>
            <div>
              <Label>Tenure</Label>
              <div className="mt-2 h-10 rounded-md border border-border bg-secondary/40 px-3 flex items-center text-sm">{tenure} mo · {(tenure / 12).toFixed(1)} yr</div>
            </div>
            <div>
              <Label>Assumed return %</Label>
              <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="sip-rate-input" />
            </div>
            <div>
              <Label>Mode</Label>
              <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={mode} onChange={(e) => setMode(e.target.value)} data-testid="sip-mode-select">
                <option>Flat SIP</option><option>Step-Up SIP</option>
              </select>
            </div>
          </div>

          {mode === "Step-Up SIP" && (
            <div className="md:w-1/2">
              <Label>Annual step-up %</Label>
              <Input type="number" step="0.5" value={step} onChange={(e) => setStep(e.target.value)} data-testid="sip-step-input" />
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Assumes SIP is invested at the <b>start of each month</b>.
          </p>

          {result && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="sip-result">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="overline mb-1 text-primary">Flat SIP</div>
                  <div className="font-dashboard text-2xl font-bold" data-testid="sip-flat-amount">{inr(result.flat_monthly_sip)}/mo</div>
                  <div className="text-xs text-muted-foreground">constant every month</div>
                </div>
                {mode === "Step-Up SIP" && result.year1_monthly_sip != null && (
                  <div>
                    <div className="overline mb-1 text-accent">Step-Up · Year 1</div>
                    <div className="font-dashboard text-2xl font-bold text-accent" data-testid="sip-step-year1">{inr(result.year1_monthly_sip)}/mo</div>
                    <div className="text-xs text-muted-foreground">stepping up {step}% each year</div>
                  </div>
                )}
              </div>
              {mode === "Step-Up SIP" && result.year_wise_sip_schedule && (
                <div className="mt-5 overflow-hidden rounded-md border border-border">
                  <table className="w-full text-xs" data-testid="sip-schedule-table">
                    <thead className="bg-secondary/60 text-muted-foreground">
                      <tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-right">Monthly SIP</th></tr>
                    </thead>
                    <tbody>
                      {result.year_wise_sip_schedule.map((row) => (
                        <tr key={row.year} className="border-t border-border">
                          <td className="px-3 py-1.5">Year {row.year}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{inr(row.monthly_sip)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={busy || !result} className="gap-2" data-testid="sip-save-btn">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save funding plan on goal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
